import { useState, useEffect, useCallback } from 'react'
import { authService, type AuthResponse } from '@/services/api'
import type { User } from '@/types'

interface RegisterData {
  email: string
  password: string
  password_confirmation: string
  username: string
  first_name?: string
  last_name?: string
  accept_terms: boolean
  plan_id: string
}

interface UseAuthReturn {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<AuthResponse>
  logout: () => Promise<void>
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token')
      const refreshToken = localStorage.getItem('refresh_token')

      if (token && refreshToken) {
        try {
          const userData = await authService.getCurrentUser()
          setUser(userData)
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      }
      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const user = await authService.login({ email, password })
      setUser(user)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true)
    try {
      const response = await authService.register(data)
      // Store user data from response
      setUser(response.user)
      // Return the full response including checkout_url if present
      return response
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setIsLoading(true)
    try {
      await authService.logout()
    } finally {
      setUser(null)
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setIsLoading(false)
    }
  }, [])

  return {
    user,
    isLoading,
    isAuthenticated: !!user || !!localStorage.getItem('access_token'),
    login,
    register,
    logout,
  }
}